/**
 * Tracked-Pathogens selection helpers (CD tidy-up 2026-06-14, target C).
 *
 * The Tracked-Pathogens legend nests pathogens by CATEGORY (transmission-route
 * / taxonomy family, derived from familyFor() in OutbreakMap — a real
 * catalog-driven field, NOT an invented grouping) and exposes a global
 * Select-all / Unselect-all plus per-category controls with an explicit
 * tri-state (all / some / none) indicator.
 *
 * "Selected" is the inverse of the map's `hidden` Set (a pid in `hidden` is
 * unticked → its markers are suppressed). These pure helpers compute the
 * tri-state and the next `hidden` Set, so the partial-selection behaviour is
 * unit-testable without React. No data is fabricated here — the pid lists come
 * from the live feed.
 */

export type TriState = 'all' | 'some' | 'none';

/** Tri-state over a pid list, given the current `hidden` set. */
export function triStateOf(pids: readonly string[], hidden: ReadonlySet<string>): TriState {
  if (pids.length === 0) return 'none';
  const shown = pids.reduce((n, p) => n + (hidden.has(p) ? 0 : 1), 0);
  return shown === pids.length ? 'all' : shown === 0 ? 'none' : 'some';
}

/** Next `hidden` set after selecting (or hiding) every pid in the list. */
export function setManyHidden(
  prev: ReadonlySet<string>,
  pids: readonly string[],
  selected: boolean,
): Set<string> {
  const n = new Set(prev);
  for (const p of pids) { if (selected) n.delete(p); else n.add(p); }
  return n;
}

/** The toggle target for a tri-state control: anything-not-all selects all,
 *  fully-selected clears. Mirrors the CD handoff's TrackedPathogens behaviour. */
export function toggleTarget(state: TriState): boolean {
  return state !== 'all';
}
